import React, { useMemo } from 'react';
import usePhysicianDashboardUser from '../../Hooks/usePhysicianDashboardUser';
import InvalidToken from '../InvalidToken';
import { Divider, Grid, Stack } from '@mui/material';
import LandingPageHeader from './LandingPageHeader';
import { useGetCampaignSummariesQuery, useGetItemReplacementsQuery } from '../../../api/physicianDashboardSlice';
import { useGetPacksSummaryQuery } from '../../../api/packSlice.js';
import { theme } from '../../../theme';
import { useState } from 'react';
import CampaignSummaryTabs from './CampaignSummaryTabs';
import CurrentCampaignTab from './CurrentCampaignTab';
import PastCampaignTab from './PastCampaignTab.jsx';

function CampaignLandingPage() {
    const { dashboardUser: physician } = usePhysicianDashboardUser();
    const [selectedTab, setSelectedTab] = useState(0);
    const { 
        data: currentCampaigns = [],
        isError: campaignsError,
        isLoading: isLoadingCampaigns
    } = useGetCampaignSummariesQuery({physicianId: physician.id, status: 'active'}, {
        skip: !physician
    });

    const {
        data: previousCampaigns = [], 
        isError: previousCampaignsError,
    } = useGetCampaignSummariesQuery({physicianId: physician.id, status: 'complete', maxDaysInPast: 90}, {
        skip: !physician
    });

    const { data: packsSummary = {}, isLoading: isLoadingPacks } = useGetPacksSummaryQuery(physician?.id, {
        skip: !physician
    });
    const { data: replacements = [], isLoading: isLoadingReplacements } = useGetItemReplacementsQuery(physician?.id, {
        skip: !physician
    });

    const replacementsSummary = useMemo(() => {
        const recommendations = replacements.flatMap(({ recommendations = [] }) => recommendations);
        const cards = new Set(recommendations.map(({ card }) => card?.id).filter(Boolean));
        const hospitals = new Set(recommendations.map(({ card }) => card?.hospital?.id ?? card?.hospital?.name).filter(Boolean));
        const openReplacements = replacements.filter(({ replacement_status }) => replacement_status === 'open').length;

        return {
            physician_id: physician?.id,
            replacements_total_count: replacements.length,
            replacements_open_count: openReplacements,
            cards_total_count: cards.size,
            hospitals_total_count: hospitals.size,
            total_savings: recommendations.reduce((total, { recommendation_value = 0 }) => total + recommendation_value, 0)
        };
    }, [physician?.id, replacements]);

    if (!physician || campaignsError || previousCampaignsError) {
        return(
            <InvalidToken />  
        )
    }

    const sortedCampaigns = currentCampaigns
        ? [...currentCampaigns].sort((a, b) => b.total_capturable_opportunity - a.total_capturable_opportunity)
        : null;

    return (
        <Grid container sx={{ backgroundColor: `${theme.palette.background.white}` }}>
            <Grid item md={3} />
            <Grid item xs={12} md={6}>
                <Stack spacing={0} sx={{ backgroundColor: `${theme.palette.background.white}` }}>
                    <LandingPageHeader physician={physician} />
                    <Grid item pb={1} xs={12} mt={0} px={2} >
                        <CampaignSummaryTabs selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
                        <Divider />
                    </Grid>
                    { selectedTab === 0 && 
                        <CurrentCampaignTab
                            campaigns={sortedCampaigns}
                            packsSummary={packsSummary}
                            replacementsSummary={replacementsSummary}
                            isLoading={isLoadingPacks || isLoadingReplacements || isLoadingCampaigns}
                        />
                    }
                    { selectedTab === 1 &&
                        <PastCampaignTab campaigns={previousCampaigns} />
                    }
                </Stack>
            </Grid>
        </Grid>
    )
}


export default CampaignLandingPage;
